import TR from 'utils/TR';
import Header from 'components/header';
import T from 'utils/T';
import ex from 'utils/ex';
import PageTitle from '../PageTitle';
import Segment from 'components/common/Segment';
import HBox from 'components/common/HBox';
import VBox from 'components/common/VBox';
import FocusableButton from 'components/common/FocusableButton';
import EditArea from './EditArea';
import ListArea from './ListArea';
import useForm from 'hooks/common/useForm';
import React from 'react';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';
import useDoctorGuide from 'hooks/communication/useDoctorGuide';
import useDoctorGuideSave from 'hooks/communication/useDoctorGuideSave';
import useMessageBox from 'hooks/app/useMessageBox';
import useDetectEffect from 'hooks/common/useDetectEffect';

export default function DrGuide(props) {
  const {
    renderAll,
    category,
    front,
    ...rest
  } = props;

  const [guide, setGuide] = React.useState({});

  const doctor_info = Recoil.useRecoilValue(AppStore.assets.doctor_info);
  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  const {
    dr_id,
  } = doctor_info || {};

  const {
    requestDoctorGuide,
    onDoctorGuide,
  } = useDoctorGuide();

  const {
    onDoctorGuideSave,
  } = useDoctorGuideSave();

  const {
    data,
    errors,
    register,
    validate,
    onSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      active_guide: '',
      meal_guide: '',
      insulin_guide: '',
    },
  });

  const [current_time, setCurrentTime] = React.useState(null);
  const onCurrentTime = React.useCallback((guide_time) => () => {
    setCurrentTime(guide_time);
  }, []);

  const [reload, setReload] = Recoil.useRecoilState(AppStore.assets.guide_reload);
  React.useEffect(() => {
    if (!current_time) return;
    const timeoutId = setTimeout(() => {
      requestDoctorGuide({
        dr_id,
        patient_no,
        new_flag_yn: 'N',
        ...(current_time ? { guide_time: current_time } : {}),
      }, (success, data, parameters) => {
        if (success) {
          setGuide(() => data);
          setValue('active_guide', data?.draft?.active_guide || '');
          setValue('meal_guide', data?.draft?.meal_guide || '');
          setValue('insulin_guide', data?.draft?.insulin_guide || '');
          setCurrentTime(() => data.draft.guide_time);
        }
      });
    }, 200);
    return () => clearTimeout(timeoutId);
  }, [current_time, dr_id, patient_no, setValue, reload, requestDoctorGuide]);

  const {
    open,
    update,
    close,
  } = useMessageBox(category);

  const onCreated = React.useCallback((success, data, parameters) => {
    if (success) {
      // setReload(r => r + 1);
    } else {
      open({
        id: 'save',
        title: T('Dr.Guide 생성 실패'),
        contents: (
          <div>
            {data.message}
          </div>
        ),
        buttons: [ex('primary', {
          autoFocus: true,
          caption: T('확인'),
          event: 'onClose',
        })],
        events: {
          onClose: () => close('save'),
        },
      });
    }
    setCurrentTime(() => null);
    setReload(r => r + 1);
  }, [close, open, setReload]);

  const onSaved = React.useCallback((success, data, parameters) => {
    if (success) {
      open({
        id: 'save',
        title: T('Dr.Guide 저장'),
        contents: (
          <div>
            {T('Dr.Guide 저장을 성공했습니다.')}
          </div>
        ),
        buttons: [ex('primary', {
          autoFocus: true,
          caption: T('확인'),
          event: 'onClose',
        })],
        events: {
          onClose: () => close('save'),
        },
      });
      setReload(r => r + 1);
    } else {
      open({
        id: 'save',
        title: T('Dr.Guide 저장 실패'),
        contents: (
          <div>
            {data.message}
          </div>
        ),
        buttons: [ex('primary', {
          autoFocus: true,
          caption: T('확인'),
          event: 'onClose',
        })],
        events: {
          onClose: () => close('save'),
        },
      });
    }
  }, [close, open, setReload]);

  const onPublished = React.useCallback((success, data, parameters) => {
    if (success) {
      open({
        id: 'publish',
        title: T('Dr.Guide 발행'),
        contents: (
          <div>
            {T('Dr.Guide 발행을 성공했습니다.')}
          </div>
        ),
        buttons: [ex('primary', {
          autoFocus: true,
          caption: T('확인'),
          event: 'onClose',
        })],
        events: {
          onClose: () => close('publish'),
        },
      });
      setReload(r => r + 1);
    } else {
      open({
        id: 'publish',
        title: T('Dr.Guide 발행 실패'),
        contents: (
          <div>
            {data.message}
          </div>
        ),
        buttons: [ex('primary', {
          autoFocus: true,
          caption: T('확인'),
          event: 'onClose',
        })],
        events: {
          onClose: () => close('publish'),
        },
      });
    }
  }, [close, open, setReload]);

  return (
    <div {...ex(['Page', 'DrGuide'], rest)}>
      <Segment>
        <HBox full>
          <ListArea {...{ category, current_time, onCurrentTime, guide, reload, setReload }} />
          <VBox full>
            <HBox>
              <HBox>
                <FocusableButton
                  {...ex('NewPostButton')}
                  {...{
                    onClick: onDoctorGuide({
                      dr_id,
                      patient_no,
                      new_flag_yn: 'Y',
                    }, onCreated)
                  }}
                >
                  새글쓰기
                </FocusableButton>
                <FocusableButton
                  {...ex('TempSaveButton')}
                  {...{
                    onClick: onSubmit(onDoctorGuideSave({ dr_id, patient_no, send_yn: 'N', guide_time: current_time, }, onSaved)),
                  }}
                >
                  임시저장
                </FocusableButton>
              </HBox>
              <HBox>
                <FocusableButton
                  {...ex('GuidePublishButton')}
                  {...{
                    onClick: onSubmit(onDoctorGuideSave({ dr_id, patient_no, send_yn: 'Y', guide_time: current_time, }, onPublished)),
                  }}
                >
                  발행하기
                </FocusableButton>
              </HBox>
            </HBox>
            <VBox full>
              <EditArea
                {...{
                  id: 'meal_guide',
                  caption: T('식사 가이드'),
                  disabled: !current_time,
                  register,
                }}
              />
              <EditArea
                {...{
                  id: 'active_guide',
                  caption: T('활동 가이드'),
                  disabled: !current_time,
                  register,
                }}
              />
              <EditArea
                {...{
                  id: 'insulin_guide',
                  caption: T('인슐린 가이드'),
                  disabled: !current_time,
                  register,
                }}
              />
            </VBox>
          </VBox>
        </HBox>
      </Segment>
    </div>
  );
}
